import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'

const sortByPosition = (aRow, bRow, dir) => dir * (aRow.position - bRow.position)

const formatOneDecimal = value => (Number.isFinite(value) && value.toFixed(1)) || value

const sortByCellValue = index => (aRow, bRow, dir) =>
  dir * (aRow.cells[index].value + aRow.position / 10000 - (bRow.cells[index].value + bRow.position / 10000))

const sortByCellValueDesc = index => (aRow, bRow, dir) =>
  dir * (bRow.cells[index].value + bRow.position / 10000 - (aRow.cells[index].value + aRow.position / 10000))

export const calcQuadrantItemsListProps = ({
  id,
  answer,
  questionVariants,
  batches,
  quadrantBrandsValue,
  quadrantXAxisValue,
  quadrantYAxisValue,
  quadrantRadiusValue,
  quadrantColorValue,
  chartLegendMode,
  colorGradient,
  onSelectPoint
}) => {
  const isLoading = 'loading' === answer

  const responses = answer?.total || 0
  const meanList = answer?.meanList || null
  const scoreList = answer?.mds?.scoresSmoothed || null

  const min = answer?.meanStat?.min || 0
  const max = answer?.meanStat?.max || 0

  const bounds =
    0 === min && 0 === max
      ? ''
      : answer?.mds?.version !== undefined && 3 <= answer.mds.version
        ? `Mean BA scores by tested attributes, ranging: ${min.toFixed(1)}-${max.toFixed(1)}`
        : `Mean scores by tested attributes, ranging: ${min.toFixed(1)}-${max.toFixed(1)}`

  const bMin = answer?.meanStat?.attr.min[`b${quadrantColorValue}`] || 0
  const bMax = answer?.meanStat?.attr.max[`b${quadrantColorValue}`] || 0

  // the default Y axis is the second attribute, which a one-attribute question does not have
  const axisTitle = value => processText('none' === value ? 'none' : batches[Number(value) - 1]?.text) || '&nbsp;'

  const cols = [
    {
      type: 'title',
      title: 'Entities',
      sort: sortByPosition,
      format: formatOneDecimal
    },
    {
      cols: [
        { type: 'value', title: axisTitle(quadrantXAxisValue), sort: sortByCellValue(1), format: formatOneDecimal },
        { type: 'value', title: axisTitle(quadrantYAxisValue), sort: sortByCellValue(2), format: formatOneDecimal },
        { type: 'value', title: axisTitle(quadrantRadiusValue), format: formatOneDecimal },
        { type: 'value', title: axisTitle(quadrantColorValue), format: formatOneDecimal }
      ],
      type: 'value',
      title: bounds,
      width: 520,
      appendix: `${formatAmounts(responses)} ${pluralize(responses, 'response', 'responses')}`
    }
  ]

  const rows = questionVariants.map(qv => {
    const qvId = `qv${qv.position}`

    const row = {
      cells: [
        {
          title: `${qv.position}. ${processText(qv.text)}${'long' !== chartLegendMode ? ` (E${qv.position})` : ''}`,
          color:
            ('none' !== quadrantColorValue &&
              colorGradient(bMin, bMax, meanList?.[qvId]?.[`b${quadrantColorValue}`] || 0)) ||
            null,
          colorIndex: ((qv.position - 1) % 21) + 1,
          onClick: onSelectPoint
        },
        { value: '-', loading: isLoading },
        { value: '-', loading: isLoading },
        { value: '-', loading: isLoading },
        { value: '-', loading: isLoading }
      ],
      disabled: 0 < quadrantBrandsValue.length && !quadrantBrandsValue.includes(String(qv.position)),
      position: qv.position
    }

    if (meanList) {
      if (scoreList) {
        row.cells[1].value = scoreList[qv.position - 1][quadrantXAxisValue - 1]
        row.cells[2].value = scoreList[qv.position - 1][quadrantYAxisValue - 1]
        row.cells[3].value = 'none' === quadrantRadiusValue ? '-' : scoreList[qv.position - 1][quadrantRadiusValue - 1]
        row.cells[4].value = 'none' === quadrantColorValue ? '-' : scoreList[qv.position - 1][quadrantColorValue - 1]
      } else {
        // characterization: coffee's third (raw-answer) branch is guarded by `responses?`, which is
        // always true here since `responses` is seeded with `|| 0` above — that branch is unreachable,
        // this `meanList` path is the only one ever taken.
        row.cells[1].value = meanList[qvId]?.[`b${quadrantXAxisValue}`] || 'n/a'
        row.cells[2].value = meanList[qvId]?.[`b${quadrantYAxisValue}`] || 'n/a'
        row.cells[3].value = 'none' === quadrantRadiusValue ? '-' : meanList[qvId]?.[`b${quadrantRadiusValue}`] || 'n/a'
        row.cells[4].value = 'none' === quadrantColorValue ? '-' : meanList[qvId]?.[`b${quadrantColorValue}`] || 'n/a'

        row.cells[1].total = answer[qvId]?.[`b${quadrantXAxisValue}`].total || 0
        row.cells[2].total = answer[qvId]?.[`b${quadrantYAxisValue}`].total || 0
        row.cells[3].total = ('none' !== quadrantRadiusValue && answer[qvId]?.[`b${quadrantRadiusValue}`].total) || 0
        row.cells[4].total = ('none' !== quadrantColorValue && answer[qvId]?.[`b${quadrantColorValue}`].total) || 0
      }
    }

    if (Object.hasOwn(qv, 'image')) {
      row.cells[0].image = qv.image
    }

    return row
  })

  if (meanList) {
    if ('none' !== quadrantRadiusValue) cols[1].cols[2].sort = sortByCellValueDesc(3)
    if ('none' !== quadrantColorValue) cols[1].cols[3].sort = sortByCellValueDesc(4)
  }

  return { id, cols, rows, classNames: ['plain'] }
}
